import * as THREE from 'three';
import { Units } from '../utils/Units';

export type ClubId =
  | 'driver'
  | '3-wood'
  | '5-iron'
  | '7-iron'
  | 'pitching-wedge'
  | 'sand-wedge'
  | 'lob-wedge'
  | 'putter';

export interface LaunchCharacteristics {
  readonly launchAngleDegrees: number;
  readonly minBallSpeedMetersPerSecond: number;
  readonly maxBallSpeedMetersPerSecond: number;
  readonly tempoBoost: number;
  readonly powerLaunchDeltaDegrees: number;
}

export interface SpinProfile {
  readonly backspinRpm: number;
  readonly sidespinSensitivity: number;
}

export interface ClubDefinition {
  readonly id: ClubId;
  readonly name: string;
  readonly loftDegrees: number;
  readonly expectedCarryYards: number;
  readonly launch: LaunchCharacteristics;
  readonly spin: SpinProfile;
  readonly dispersionTendency: number;
  readonly rolloutTendency: number;
}

export const CLUBS: readonly ClubDefinition[] = [
  {
    id: 'driver',
    name: 'Driver',
    loftDegrees: 10.5,
    expectedCarryYards: 240,
    launch: {
      launchAngleDegrees: 12,
      minBallSpeedMetersPerSecond: 38,
      maxBallSpeedMetersPerSecond: 87,
      tempoBoost: 0.1,
      powerLaunchDeltaDegrees: 1.5,
    },
    spin: { backspinRpm: 2600, sidespinSensitivity: 0.9 },
    dispersionTendency: 1.25,
    rolloutTendency: 1.35,
  },
  {
    id: '3-wood',
    name: '3 Wood',
    loftDegrees: 15,
    expectedCarryYards: 218,
    launch: {
      launchAngleDegrees: 13.5,
      minBallSpeedMetersPerSecond: 35,
      maxBallSpeedMetersPerSecond: 74,
      tempoBoost: 0.08,
      powerLaunchDeltaDegrees: 1.25,
    },
    spin: { backspinRpm: 3400, sidespinSensitivity: 0.8 },
    dispersionTendency: 1.05,
    rolloutTendency: 1.05,
  },
  {
    id: '5-iron',
    name: '5 Iron',
    loftDegrees: 27,
    expectedCarryYards: 178,
    launch: {
      launchAngleDegrees: 16,
      minBallSpeedMetersPerSecond: 30,
      maxBallSpeedMetersPerSecond: 57,
      tempoBoost: 0.04,
      powerLaunchDeltaDegrees: 1,
    },
    spin: { backspinRpm: 5200, sidespinSensitivity: 0.65 },
    dispersionTendency: 0.85,
    rolloutTendency: 0.75,
  },
  {
    id: '7-iron',
    name: '7 Iron',
    loftDegrees: 34,
    expectedCarryYards: 152,
    launch: {
      launchAngleDegrees: 21,
      minBallSpeedMetersPerSecond: 26,
      maxBallSpeedMetersPerSecond: 48,
      tempoBoost: 0.02,
      powerLaunchDeltaDegrees: 0.75,
    },
    spin: { backspinRpm: 6500, sidespinSensitivity: 0.55 },
    dispersionTendency: 0.7,
    rolloutTendency: 0.5,
  },
  {
    id: 'pitching-wedge',
    name: 'Pitching Wedge',
    loftDegrees: 46,
    expectedCarryYards: 118,
    launch: {
      launchAngleDegrees: 32,
      minBallSpeedMetersPerSecond: 18,
      maxBallSpeedMetersPerSecond: 35,
      tempoBoost: 0,
      powerLaunchDeltaDegrees: 0.5,
    },
    spin: { backspinRpm: 8500, sidespinSensitivity: 0.42 },
    dispersionTendency: 0.55,
    rolloutTendency: 0.25,
  },
  {
    id: 'sand-wedge',
    name: 'Sand Wedge',
    loftDegrees: 56,
    expectedCarryYards: 90,
    launch: {
      launchAngleDegrees: 37,
      minBallSpeedMetersPerSecond: 14,
      maxBallSpeedMetersPerSecond: 29,
      tempoBoost: 0,
      powerLaunchDeltaDegrees: 0.4,
    },
    spin: { backspinRpm: 9200, sidespinSensitivity: 0.38 },
    dispersionTendency: 0.5,
    rolloutTendency: 0.18,
  },
  {
    id: 'lob-wedge',
    name: 'Lob Wedge',
    loftDegrees: 60,
    expectedCarryYards: 72,
    launch: {
      launchAngleDegrees: 41,
      minBallSpeedMetersPerSecond: 12,
      maxBallSpeedMetersPerSecond: 25,
      tempoBoost: 0,
      powerLaunchDeltaDegrees: 0.3,
    },
    spin: { backspinRpm: 9600, sidespinSensitivity: 0.36 },
    dispersionTendency: 0.48,
    rolloutTendency: 0.12,
  },
  {
    id: 'putter',
    name: 'Putter',
    loftDegrees: 3,
    expectedCarryYards: 0,
    launch: {
      launchAngleDegrees: 1,
      minBallSpeedMetersPerSecond: 1,
      maxBallSpeedMetersPerSecond: 5,
      tempoBoost: 0,
      powerLaunchDeltaDegrees: 0.5,
    },
    spin: { backspinRpm: 0, sidespinSensitivity: 0.2 },
    dispersionTendency: 0.25,
    rolloutTendency: 0.15,
  },
] as const;

export function clubAtOffset(current: ClubDefinition, offset: number): ClubDefinition {
  const currentIndex = CLUBS.findIndex((club) => club.id === current.id);
  const nextIndex = THREE.MathUtils.euclideanModulo(currentIndex + offset, CLUBS.length);
  return CLUBS[nextIndex];
}

export function expectedCarryMeters(club: ClubDefinition): number {
  return Units.yardsToMeters(club.expectedCarryYards);
}
